import { useState, useRef } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import { query } from '@/theme/breakpoints';
import { TextField, Button } from '@/ui';
import { Icon } from '@/icons';
import { useSession, useInviteInfo } from '@/hooks';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/core/lib/apiClient';
import { validateSignUp, type FieldErrors } from './validation';

// Invite codes are 10 chars from a 32-char unambiguous alphabet server-side.
// Anything else in ?invite= is not a code — drop it silently rather than
// render a chip for garbage (or ship it to the server just to hear "invalid").
const INVITE_CODE_RE = /^[A-Za-z0-9]{6,32}$/;
function readInviteParam(raw: string | null): string | null {
  const v = (raw ?? '').trim();
  return INVITE_CODE_RE.test(v) ? v : null;
}

const Form = styled.form`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media ${query.mobile} {
    margin-top: 16px;
  }
`;
/* Two groups: who you are and how you sign in; then the two that must match. */
const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const GroupB = styled(Group)`
  margin-top: 12px;
  @media ${query.mobile} {
    margin-top: 8px;
  }
`;
/* The one accent fill on the page. Taller and set in the working size so it
   outranks every other control by construction, not by decoration. */
const Submit = styled(Button)`
  width: 100%;
  height: 44px;
  padding: 0 16px;
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-top: 8px;
  @media ${query.mobile} {
    margin-top: 4px;
  }
`;
const Banner = styled.div`
  color: ${({ theme }) => theme.color.danger};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const Check = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  margin-top: 8px;
  line-height: 20px;
  input { width: 16px; height: 16px; margin: 2px 0 0; accent-color: ${({ theme }) => theme.color.textPrimary}; }
  /* The two policy links. They had no rule and fell through to the browser's
     own blue, the oldest unstyled-anchor tell there is. */
  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }
`;
const Sent = styled.div`
  display: flex; flex-direction: column; gap: 12px; text-align: center;
`;
const SentTitle = styled.h2`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; color: ${({ theme }) => theme.color.textPrimary};
`;
const SentBody = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};
`;
const SignInCta = styled(Link)`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-top: 6px;
  font-weight: 600; font-size: ${({ theme }) => theme.fontSize.sm}; padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md}; text-decoration: none; color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.accentCyan};
`;
// 1.24.00 invites — the "Invited by @x" chip. Dismissing it DROPS the code
// (the member may not want the inviter credited); the form still submits.
const InviteChip = styled.div`
  display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
  height: 28px; padding: 0 8px 0 12px; border-radius: ${({ theme }) => theme.radius.md};
  /* Ink, not accent: an invite is information, not the page's one action. */
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};
  svg:first-child { color: ${({ theme }) => theme.color.textMuted}; }
`;
const ChipDismiss = styled.button`
  display: inline-flex; line-height: 0; padding: 2px; border: none; background: transparent;
  cursor: pointer; color: ${({ theme }) => theme.color.textMuted}; border-radius: 50%;
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.focusRing}; outline-offset: 2px; }
`;

export interface SignUpFormProps {
  next: string | null;
}

export function SignUpForm({ next }: SignUpFormProps) {
  const { signUp } = useSession();
  const formRef = useRef<HTMLFormElement>(null);
  const [params] = useSearchParams();

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [inviteOutcome, setInviteOutcome] = useState<'applied' | 'invalid' | null>(null);
  // 1.24.00 invites: the code from ?invite=<code> (a /join/<code> landing).
  // The only STATE here is the member's dismissal — everything else is derived,
  // so there is no effect writing state back on a lookup result.
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const paramCode = readInviteParam(params.get('invite'));
  // Who the code belongs to, for the chip's "Invited by @x".
  const inviteInfo = useInviteInfo(inviteDismissed ? null : paramCode);
  // A code the server no longer knows (revoked/unknown) is dropped here rather
  // than sent along just to hear "invalid" back — derived, not stored.
  const inviteCode =
    inviteDismissed || inviteInfo.error === 'not_found' ? null : paramCode;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner('');
    const values = { fullName, username, email, password, confirmPassword, agree };
    const errs = validateSignUp(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }
    setBusy(true);
    try {
      const result = await signUp({
        fullName: fullName.trim(),
        username: username.trim(),
        email: email.trim(),
        password,
        ...(inviteCode ? { inviteCode } : {}),
      });
      setInviteOutcome(result.invite);
      setSentTo(email.trim());
    } catch (err) {
      if (err instanceof Error && err.name === 'RateLimited') {
        setBanner('Too many attempts. Please wait a moment and try again.');
      } else if (err instanceof Error && err.name === 'UsernameTaken') {
        setErrors({ username: 'That username is already taken.' });
      } else if (err instanceof ApiError && err.code === 'email_taken') {
        setErrors({ email: 'That email is already registered.' });
      } else if (err instanceof ApiError && err.code === 'email_disposable') {
        setErrors({ email: "Disposable email addresses aren't allowed." });
      } else if (err instanceof ApiError && err.code === 'email_undeliverable') {
        setErrors({ email: "We couldn't find a mail server for that domain." });
      } else if (err instanceof ApiError && err.code === 'email_required') {
        setErrors({ email: 'Email is required.' });
      } else if (err instanceof ApiError && err.code === 'validation' && (err.fields as { email?: unknown })?.email) {
        setErrors({ email: 'Enter a valid email address.' });
      } else {
        setBanner('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <Sent role="status">
        <SentTitle>Check your inbox</SentTitle>
        <SentBody>
          We sent a verification link to <strong>{sentTo}</strong>. Click it to confirm your email,
          then sign in to start posting. (Reading and exploring work right away.)
        </SentBody>
        <SentBody>Didn&apos;t get it? Check your spam folder. You can resend after signing in.</SentBody>
        {/* An unknown/revoked code never blocks a sign-up — say so in one line
            rather than let the member wonder whether the account exists. */}
        {inviteOutcome === 'invalid' && (
          <SentBody>That invite link wasn&apos;t valid, but your account is ready.</SentBody>
        )}
        <SignInCta to={next ? `/signin?next=${encodeURIComponent(next)}` : '/signin'}>Go to sign in</SignInCta>
      </Sent>
    );
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit} noValidate>
      {inviteCode && (
        <InviteChip data-testid="invite-chip">
          <Icon name="user-plus" size={14} />
          <span>
            {inviteInfo.info
              ? <>Invited by <strong>{inviteInfo.info.inviter.handle}</strong></>
              : 'Invited with a link'}
          </span>
          <ChipDismiss
            type="button"
            aria-label="Remove invite code"
            onClick={() => setInviteDismissed(true)}
          >
            <Icon name="close" size={14} />
          </ChipDismiss>
        </InviteChip>
      )}
      <Group>
        <TextField large label="Full Name" autoComplete="name" placeholder="Bruce Wayne"
          value={fullName} error={errors.fullName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFullName(e.currentTarget.value)} />
        <TextField large label="Username" autoComplete="username" placeholder="brucew"
          value={username} error={errors.username}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.currentTarget.value)} />
        <TextField large label="Email Address" type="email" autoComplete="email" placeholder="bruce@example.com"
          value={email} error={errors.email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.currentTarget.value)} />
      </Group>
      <GroupB>
        <TextField large label="Password" type="password" autoComplete="new-password" placeholder="At least 8 characters"
          value={password} error={errors.password}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value)} />
        <TextField large label="Confirm Password" type="password" autoComplete="new-password" placeholder="Repeat your password"
          value={confirmPassword} error={errors.confirmPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.currentTarget.value)} />
      </GroupB>
      {/* This checkbox blocked account creation while the documents it named did
          not exist and were not linked — an attestation nobody could honestly
          make. The links are the fix; do not remove them without removing the
          gate too. They open in a new tab so a half-filled form is not lost. */}
      <Check>
        <input type="checkbox" checked={agree}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setAgree(e.currentTarget.checked)}
          aria-label="I agree to the Terms and Privacy Policy" />
        <span>
          I agree to the{' '}
          <a href="/policies/terms" target="_blank" rel="noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="/policies/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </span>
      </Check>
      {errors.agree && <Banner role="alert">{errors.agree}</Banner>}
      {banner && <Banner role="alert">{banner}</Banner>}
      <Submit type="submit" variant="primary" disabled={busy}>Create Account</Submit>
    </Form>
  );
}
